import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Phone, Mail, MapPin, Lock, User, CheckCircle, ArrowRight, ShieldCheck, Instagram, Facebook, Linkedin, Info, MessageCircle, Send, Sparkles, Bot, Loader, FileText, ChevronLeft, Eye, Download } from 'lucide-react';

const COLORS = {
  primary: '#BE1622', 
  secondary: '#3C3C3B',
  white: '#FFFFFF',
  lightGray: '#F5F5F5',
  slate: '#E2E8F0'
};

// --- CONFIGURAZIONE IMMAGINI ---
const ASSETS = {
  logoHeader: "/assets/logo.png", 
  logoFooter: "/assets/logo-white.png", 
  imgRadiante: "/assets/radiante.jpg",
  imgPompa: "/assets/pompa.jpg",
  imgVMC: "/assets/vmc.jpg" 
};

const FALLBACK_IMAGES = {
  logo: "https://placehold.co/240x80/FFFFFF/3C3C3B?text=IARC+LOGO&font=roboto",
  radiante: "https://images.unsplash.com/photo-1581094288338-2314dddb7ece?q=80&w=800&auto=format&fit=crop",
  pompa: "https://images.unsplash.com/photo-1581092160607-ee22621dd758?q=80&w=800&auto=format&fit=crop",
  vmc: "https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?q=80&w=800&auto=format&fit=crop"
};

const handleImgError = (e, fallbackUrl) => {
  e.target.src = fallbackUrl;
};

// --- GEMINI API CONFIGURATION ---
const apiKey = ""; 

// --- COMPONENTI ---

const GeminiAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: 'model', text: 'Ciao! Sono IARC Assistant, il tuo supporto tecnico. Come posso aiutarti oggi?' }]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); };
  useEffect(() => { scrollToBottom(); }, [messages, isOpen]);

  const handleSend = async () => {
    if (!input.trim()) return;
    const userMessage = { role: 'user', text: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    try {
      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${apiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
            contents: [{ parts: [{ text: input }] }],
            systemInstruction: {
                parts: [{
                    text: `Sei "IARC Assistant", un assistente tecnico virtuale di backoffice per un'agenzia di rappresentanze commerciali nel settore termoidraulico.
                          Aziende Rappresentate: Valsir spa, Fondital spa, Far Rubinetterie Spa, Conex Bänninger, Enolgas Bonomi, Rothenberger.
                          Linee Guida: Tono professionale. Solo info tecniche generiche. Per dettagli specifici reindirizza a info@iarc.it.
                          Regole Tassative: NO PREZZI. Reindirizza all'email per quotazioni.
                          Procedura Preventivi (Radiante/VMC): Rispondi OBBLIGATORIAMENTE: "Per poter elaborare un preventivo accurato per sistemi radianti o VMC, è indispensabile analizzare una piantina dell'edificio. La preghiamo di inviare la planimetria in formato .PDF o .DWG all'indirizzo email info@iarc.it, specificando la sua richiesta. Sarà ricontattato da un nostro tecnico specializzato."`
                }]
            }
        })
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error.message);
      setMessages(prev => [...prev, { role: 'model', text: data.candidates?.[0]?.content?.parts?.[0]?.text || "Non ho capito." }]);
    } catch (error) {
      setMessages(prev => [...prev, { role: 'model', text: "Errore di connessione o limite raggiunto." }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <button onClick={() => setIsOpen(!isOpen)} className="fixed bottom-36 right-6 p-4 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 z-50 flex items-center justify-center text-white" style={{ backgroundColor: COLORS.primary }}>
        {isOpen ? <X size={28} /> : <Sparkles size={28} />}
      </button>
      {isOpen && (
        <div className="fixed bottom-56 right-6 w-80 md:w-96 h-[400px] bg-white rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden border border-gray-200">
          <div className="p-4 bg-gray-900 text-white flex justify-between"><span className="font-bold flex items-center gap-2"><Bot size={18}/> IARC AI</span></div>
          <div className="flex-1 p-4 overflow-y-auto bg-gray-50 space-y-3">
            {messages.map((msg, i) => (
              <div key={i} className={`p-2 rounded-lg text-sm max-w-[85%] ${msg.role === 'user' ? 'ml-auto bg-red-600 text-white' : 'bg-white border text-gray-800'}`}>{msg.text}</div>
            ))}
            {isLoading && <div className="text-xs text-gray-400 p-2">Sto scrivendo...</div>}
            <div ref={messagesEndRef} />
          </div>
          <div className="p-3 bg-white border-t flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleSend()} placeholder="Scrivi qui..." className="flex-1 p-2 text-sm border rounded-md" />
            <button onClick={handleSend} className="p-2 bg-red-600 text-white rounded-md"><Send size={16} /></button>
          </div>
        </div>
      )}
    </>
  );
};

const PrivacyPolicy = ({ onBack }) => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="bg-white min-h-screen py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <button onClick={onBack} className="flex items-center text-gray-600 hover:text-red-600 mb-8 transition-colors"><ChevronLeft size={20} /> Torna indietro</button>
        <h1 className="text-3xl font-extrabold text-gray-900 mb-8">Privacy Policy</h1>
        <div className="prose prose-red max-w-none text-gray-600">
          <p className="lead text-lg">Informativa sul trattamento dei dati personali ai sensi dell'art. 13 del Regolamento UE 2016/679 (GDPR).</p>
          <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">1. Titolare del Trattamento</h3>
          <p>Il Titolare del trattamento è <strong>IARC sas di Mirko Rogato & C.</strong><br/>Sede legale ed operativa: Via S.Antonio Abate, 53 - 87021 Belvedere Marittimo (CS)<br/>Contatto email: info@iarc.it</p>
          <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">2. Quali dati trattiamo e perché</h3>
          <p>Anche in assenza di moduli di contatto, il sito tratta i seguenti dati:</p>
          <ul className="list-disc pl-5 space-y-4 mt-4">
            <li><strong>Dati di navigazione (Log di sistema):</strong> Indirizzi IP, orari di connessione.</li>
            <li><strong>Dati forniti volontariamente all'Assistente AI:</strong> Messaggi inviati al chatbot.</li>
            <li><strong>Credenziali Area Riservata:</strong> Dati di autenticazione.</li>
          </ul>
          <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">3. Servizi di Terze Parti</h3>
          <ul className="list-disc pl-5 space-y-2 mt-2">
            <li><strong>Aruba S.p.A.</strong>: Hosting provider.</li>
            <li><strong>Google LLC (Gemini API)</strong>: Fornitore AI chatbot.</li>
          </ul>
          <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">4. Diritti dell'interessato</h3>
          <p>Puoi esercitare i tuoi diritti contattando il Titolare via email.</p>
          <p className="text-xs text-gray-400 mt-8">Ultimo aggiornamento: Dicembre 2025</p>
        </div>
      </div>
    </div>
  );
};

const CookiePolicy = ({ onBack }) => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="bg-white min-h-screen py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <button onClick={onBack} className="flex items-center text-gray-600 hover:text-red-600 mb-8 transition-colors"><ChevronLeft size={20} /> Torna indietro</button>
        <h1 className="text-3xl font-extrabold text-gray-900 mb-8">Cookie Policy</h1>
        <div className="prose prose-red max-w-none text-gray-600">
          <p className="lead text-lg">La presente Cookie Policy spiega come <strong>IARC sas di Mirko Rogato & C.</strong> utilizza i cookie.</p>
          <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">Cosa sono i cookie?</h3>
          <p>Piccoli file di testo inviati al terminale dell'utente per memorizzare preferenze.</p>
          <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">Quali cookie utilizziamo?</h3>
          <p>Utilizziamo esclusivamente cookie tecnici necessari.</p>
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 mt-6">
            <h4 className="font-bold text-gray-900 mb-4 flex items-center"><ShieldCheck className="w-5 h-5 text-green-600 mr-2" /> 1. Cookie Tecnici e di Sessione</h4>
            <ul className="list-disc pl-5 space-y-2 text-sm">
              <li><strong>ASPSESSIONID*</strong>: Gestione sessione Area Riservata.</li>
              <li><strong>iarc_cookie_consent</strong>: Preferenza accettazione cookie.</li>
            </ul>
          </div>
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 mt-6">
            <h4 className="font-bold text-gray-900 mb-4 flex items-center"><Bot className="w-5 h-5 text-blue-600 mr-2" /> 2. Funzionalità di Terze Parti (AI)</h4>
            <p className="text-sm mb-4">Il chatbot utilizza API di Google Gemini. Non installa cookie di profilazione persistenti.</p>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">Come gestire i cookie?</h3>
          <p>Puoi gestire le preferenze attraverso il tuo browser.</p>
          <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">Titolare del Trattamento</h3>
          <p>IARC sas di Mirko Rogato & C.<br/>Via Sant'Antonio Abate, 53 - 87021 Belvedere Marittimo (CS)<br/>Email: info@iarc.it</p>
          <p className="text-xs text-gray-400 mt-8">Ultimo aggiornamento: Dicembre 2025</p>
        </div>
      </div>
    </div>
  );
};

const CookieBanner = ({ onOpenPolicy }) => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => { if (!localStorage.getItem('iarc_cookie_consent')) setIsVisible(true); }, []);
  return isVisible ? (
    <div className="fixed bottom-0 left-0 w-full bg-gray-900 text-white p-4 z-50 shadow-2xl border-t-4 border-red-600 flex flex-col md:flex-row justify-between items-center gap-4">
      <div className="mb-4 md:mb-0 md:mr-8 text-sm text-gray-300">
          <p className="font-bold text-white mb-1">Informativa sui Cookie</p>
          <p>
            Utilizziamo cookie tecnici per garantire il funzionamento del sito (login area riservata). Chiudendo questo banner o cliccando su "Rifiuta", navigherai usando solo i cookie necessari.
            <button onClick={onOpenPolicy} className="underline text-red-400 ml-2 hover:text-red-300">Leggi Cookie Policy</button>
          </p>
        </div>
      <div className="flex gap-2 min-w-fit">
        <button onClick={() => {localStorage.setItem('iarc_cookie_consent','no'); setIsVisible(false)}} className="px-3 py-1 border rounded text-sm hover:bg-gray-800 transition">Rifiuta</button>
        <button onClick={() => {localStorage.setItem('iarc_cookie_consent','yes'); setIsVisible(false)}} className="px-3 py-1 bg-red-600 rounded text-sm font-bold hover:bg-red-700 transition">Accetta</button>
      </div>
    </div>
  ) : null;
};

const Navigation = ({ activeTab, setActiveTab }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuItems = [ { id: 'home', label: 'Home' }, { id: 'chi-siamo', label: 'Chi Siamo' }, { id: 'servizi', label: 'Settori' }, { id: 'contatti', label: 'Contatti' } ];

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-24">
          <div className="flex items-center">
            <div className="flex-shrink-0 flex items-center cursor-pointer" onClick={() => setActiveTab('home')}>
              <img src={ASSETS.logoHeader} onError={(e) => handleImgError(e, FALLBACK_IMAGES.logo)} alt="IARC Rappresentanze" className="h-16 w-auto object-contain" />
            </div>
          </div>
          <div className="hidden md:flex md:items-center md:space-x-8">
            {menuItems.map((item) => (
              <button key={item.id} onClick={() => setActiveTab(item.id)} className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === item.id ? 'text-red-700 bg-red-50' : 'text-gray-600 hover:text-red-600'}`}>{item.label}</button>
            ))}
            {/* LINK REALE ALLA CARTELLA PROTETTA DEL SERVER */}
            <a 
              href="/areariservata/" 
              className="flex items-center space-x-2 text-white px-4 py-2 rounded-md text-sm font-medium transition shadow-lg hover:-translate-y-0.5" 
              style={{ backgroundColor: COLORS.primary }}
            >
              <Lock size={16} /> <span>Area Riservata</span>
            </a>
          </div>
          <div className="flex items-center md:hidden">
            <button onClick={() => setIsOpen(!isOpen)} className="p-2 text-gray-400"><Menu size={24} /></button>
          </div>
        </div>
      </div>
      {isOpen && (
        <div className="md:hidden bg-white border-t p-2">
          {menuItems.map((item) => (
            <button key={item.id} onClick={() => { setActiveTab(item.id); setIsOpen(false); }} className="block w-full text-left px-3 py-2 text-gray-700 font-medium">{item.label}</button>
          ))}
          <a href="/areariservata/" className="block w-full text-left px-3 py-2 text-red-600 font-bold bg-red-50 mt-2">Area Riservata</a>
        </div>
      )}
    </nav>
  );
};

const Hero = ({ setActiveTab }) => (
  <div className="bg-white">
    <div className="max-w-7xl mx-auto lg:grid lg:grid-cols-2 lg:gap-8">
      
      <div className="relative z-10 pb-8 bg-white sm:pb-16 md:pb-20 lg:pb-28 xl:pb-32 pt-10 px-4 sm:px-6 lg:px-8 flex flex-col justify-center">
          <div className="sm:text-center lg:text-left">
            <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
              <span className="block xl:inline">Soluzioni integrate per</span>{' '}
              <span className="block xl:inline" style={{ color: COLORS.primary }}>termoidraulica e climatizzazione professionale.</span>
            </h1>
            <p className="mt-3 text-base text-gray-500 sm:mt-5 sm:text-lg sm:max-w-xl sm:mx-auto md:mt-5 md:text-xl lg:mx-0">
              IARC Rappresentanze: l’evoluzione della consulenza tecnica per installatori e studi di progettazione in Calabria, radicata in un’esperienza consolidata nel settore termoidraulico e focalizzata su soluzioni impiantistiche efficienti.
            </p>
            <div className="mt-5 sm:mt-8 sm:flex sm:justify-center lg:justify-start gap-3">
              <button onClick={() => setActiveTab('contatti')} className="flex justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-red-700 hover:bg-red-800 md:py-4 md:text-lg">Contattaci</button>
              <button onClick={() => setActiveTab('servizi')} className="flex justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 md:py-4 md:text-lg">I nostri servizi</button>
            </div>
          </div>
      </div>

      <div className="relative lg:inset-y-0 lg:right-0 lg:w-full bg-gray-50 flex flex-col h-[500px] lg:h-auto">
        <div className="h-full w-full grid grid-rows-3 gap-1 p-1">
          {[
            { src: ASSETS.imgRadiante, fallback: FALLBACK_IMAGES.radiante, cat: "Sistemi", title: "Radiante a Pavimento" },
            { src: ASSETS.imgPompa, fallback: FALLBACK_IMAGES.pompa, cat: "Climatizzazione", title: "Pompe di Calore" },
            { src: ASSETS.imgVMC, fallback: FALLBACK_IMAGES.vmc, cat: "Trattamento Aria", title: "Ventilazione Meccanica Controllata" }
          ].map((item, idx) => (
            <div key={idx} className="relative w-full h-full overflow-hidden group">
              <img src={item.src} onError={(e) => handleImgError(e, item.fallback)} alt={item.title} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors"></div>
              <div className="absolute bottom-0 right-0 text-white p-4 text-right">
                 <p className="text-xs font-bold uppercase tracking-wider mb-1 opacity-80 drop-shadow-md">{item.cat}</p>
                 <p className="font-bold text-lg leading-tight drop-shadow-md">{item.title}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  </div>
);

const About = () => (
  <div className="py-16 bg-white px-4">
    <div className="max-w-7xl mx-auto">
      <div className="lg:text-center mb-10">
        <h2 className="text-base font-semibold tracking-wide uppercase text-red-600">La Nostra Storia</h2>
        <p className="mt-2 text-3xl font-extrabold text-gray-900">Eredità ed Innovazione</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="bg-gray-50 p-8 rounded-lg border-l-4 border-red-600">
          <h3 className="text-2xl font-bold mb-4">Le Origini</h3>
          <p className="text-gray-600">L'agenzia raccoglie l'eredità e l'esperienza della precedente agenzia IARC, attiva dal 1975.</p>
        </div>
        <div className="bg-gray-50 p-8 rounded-lg border-l-4 border-gray-800">
          <h3 className="text-2xl font-bold mb-4">La Mission</h3>
          <p className="text-gray-600">Il nostro obiettivo è fornire soluzioni complete. Partner tecnico per progettisti e installatori.</p>
        </div>
      </div>
    </div>
  </div>
);

const Services = () => {
  const services = [
    { title: "Sistemi Idrici", desc: "Soluzioni per la distribuzione e il trattamento delle acque." },
    { title: "Climatizzazione", desc: "Impianti all'avanguardia per il comfort residenziale e industriale." },
    { title: "Riscaldamento", desc: "Sistemi ad alta efficienza energetica e termoregolazione." },
    { title: "Consulenza Tecnica", desc: "Supporto alla progettazione e assistenza post-vendita." },
  ];
  return (
    <div className="py-16 bg-gray-50 px-4">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl font-extrabold text-center mb-12">Settori di Competenza</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((s, i) => (
            <div key={i} className="bg-white p-6 rounded-lg shadow text-center hover:shadow-lg transition">
              <CheckCircle className="mx-auto h-12 w-12 text-red-600 mb-4" />
              <h3 className="text-lg font-medium">{s.title}</h3>
              <p className="mt-2 text-sm text-gray-500">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Contact = () => {
  return (
    <div className="py-24 bg-white px-4">
      <div className="max-w-7xl mx-auto text-center mb-16">
        <h2 className="text-base font-semibold tracking-wide uppercase" style={{ color: COLORS.primary }}>
          Parla con noi
        </h2>
        <h2 className="text-4xl font-extrabold text-gray-900 tracking-tight sm:text-5xl mt-2">
          I Nostri Contatti
        </h2>
        <p className="mt-4 text-xl text-gray-500 max-w-2xl mx-auto">
          Il nostro team è pronto a fornire supporto tecnico e commerciale per ogni tua esigenza.
        </p>
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-10">
        
        <div className="bg-gray-50 p-8 rounded-2xl text-center hover:shadow-xl transition border group">
          <MapPin className="mx-auto h-10 w-10 text-red-600 mb-4" />
          <h3 className="text-xl font-bold mb-2">Sede Operativa</h3>
          <p className="text-gray-600">Via G. Fiorillo, snc<br/>Belvedere Marittimo (CS)</p>
          <a 
            href="https://maps.app.goo.gl/rwTpwCjM5VSLkrR19" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center mt-6 text-red-600 font-bold hover:text-red-800 transition-colors"
          >
            Vedi su Mappa <ArrowRight size={16} className="ml-2" />
          </a>
        </div>

        <div className="bg-gray-50 p-8 rounded-2xl text-center hover:shadow-xl transition border group">
          <Phone className="mx-auto h-10 w-10 text-red-600 mb-4" />
          <h3 className="text-xl font-bold mb-2">Telefono</h3>
          <p className="text-gray-600 mb-4">Lunedì - Venerdì<br/>9:00 - 12:00 / 15:00 - 18:00</p>
          <a 
            href="tel:+39098582765" 
            className="text-lg font-bold text-gray-900 hover:text-red-600 transition-colors block mt-2 whitespace-nowrap"
          >
            +39 0985 82765
          </a>
        </div>

        <div className="bg-gray-50 p-8 rounded-2xl text-center hover:shadow-xl transition border group">
          <Mail className="mx-auto h-10 w-10 text-red-600 mb-4" />
          <h3 className="text-xl font-bold mb-2">Email</h3>
          <p className="text-gray-600 mb-4">Per informazioni,<br/>preventivi e consulenza</p>
          <a 
            href="mailto:info@iarc.it" 
            className="text-2xl font-bold text-red-600 hover:text-red-800 transition-colors break-all"
          >
            info@iarc.it
          </a>
        </div>

      </div>

      <div className="mt-16 border-t border-gray-200 pt-10 text-center">
         <p className="text-gray-500 uppercase tracking-widest text-sm font-semibold mb-2">Dati Aziendali</p>
         <p className="text-gray-900 font-medium">IARC sas di Mirko Rogato & C. • P.IVA 03689190787</p>
      </div>
    </div>
  );
};

// --- LOGIN REALE (Server Side) ---
const Login = () => {
  return (
    <div className="min-h-[60vh] flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white p-10 rounded-xl shadow-xl">
        <div className="text-center mb-6">
          <Lock className="mx-auto h-12 w-12 text-red-600" />
          <h2 className="mt-4 text-3xl font-extrabold">Area Riservata</h2>
        </div>
        
        {/* FORM CHE INVIA DATI A LOGIN.ASP SUL SERVER */}
        <form action="login.asp" method="POST" className="space-y-6">
          <input type="hidden" name="act" value="login" />
          <input type="hidden" name="azione" value="login" />
          
          <input 
            type="text" 
            name="user" 
            placeholder="Username" 
            className="block w-full p-3 border rounded" 
            required 
          />
          <input 
            type="password" 
            name="pass" 
            placeholder="Password" 
            className="block w-full p-3 border rounded" 
            required 
          />
          
          <button type="submit" className="w-full py-3 bg-gray-900 text-white rounded font-bold hover:bg-gray-800 transition">
            Accedi
          </button>
        </form>
      </div>
    </div>
  );
};

const Footer = ({ setActiveTab }) => (
  <footer className="bg-gray-900 text-white py-12 px-4">
    <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
      <div>
        <img src={ASSETS.logoFooter} onError={(e) => handleImgError(e, FALLBACK_IMAGES.logo)} alt="IARC" className="h-10 w-auto mb-4 object-contain brightness-0 invert" />
        <p className="text-gray-400 text-sm">Dal 2020, il punto di riferimento per idraulica e clima.</p>
      </div>
      <div>
        <h3 className="text-sm font-bold uppercase mb-4">Link Rapidi</h3>
        <ul className="space-y-2 text-gray-300 text-sm">
          <li><button onClick={() => setActiveTab('home')} className="hover:text-white">Home</button></li>
          <li><button onClick={() => setActiveTab('chi-siamo')} className="hover:text-white">Chi Siamo</button></li>
          <li><button onClick={() => setActiveTab('contatti')} className="hover:text-white">Contatti</button></li>
        </ul>
      </div>
      <div>
        <h3 className="text-sm font-bold uppercase mb-4">Legale</h3>
        <div className="flex flex-col space-y-2 text-gray-300 text-sm">
          <button onClick={() => setActiveTab('privacy-policy')} className="text-left hover:text-white">Privacy Policy</button>
          <button onClick={() => setActiveTab('cookie-policy')} className="text-left hover:text-white">Cookie Policy</button>
        </div>
      </div>
    </div>
  </footer>
);

export default function App() {
  const [activeTab, setActiveTab] = useState('home');
  useEffect(() => { window.scrollTo(0, 0); }, [activeTab]);

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900 flex flex-col">
      <CookieBanner onOpenPolicy={() => setActiveTab('cookie-policy')} />
      <Navigation activeTab={activeTab} setActiveTab={setActiveTab} />
      <main className="flex-grow">
        {activeTab === 'home' && <><Hero setActiveTab={setActiveTab} /><About /><Services /><Contact /></>}
        {activeTab === 'chi-siamo' && <About />}
        {activeTab === 'servizi' && <Services />}
        {activeTab === 'contatti' && <Contact />}
        
        {/* LOGIN FORM (Reale) */}
        {activeTab === 'login' && <Login />}
        
        {activeTab === 'cookie-policy' && <CookiePolicy onBack={() => setActiveTab('home')} />}
        {activeTab === 'privacy-policy' && <PrivacyPolicy onBack={() => setActiveTab('home')} />}
      </main>
      <GeminiAssistant />
      <Footer setActiveTab={setActiveTab} />
    </div>
  );
}